import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { FileText, Download, CheckCircle2, ShieldAlert, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Compliance gate: shown after sign-in when today's toolbox topic exists
 * and the user has not yet acknowledged it. User MUST acknowledge
 * before they can clock in / continue to the dashboard.
 *
 * Props:
 *  - token (string)
 *  - topic (object: {id, title, content_text, date, content_pdf_filename, ...})
 *  - onAcknowledged () => void
 */
export default function ToolboxAckGate({ token, topic, onAcknowledged }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (!topic?.content_pdf_filename) return;
    let revoke = null;
    (async () => {
      try {
        const res = await axios.get(`${API}/toolbox/pdf/${topic.content_pdf_filename}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        revoke = url;
        setPdfUrl(url);
      } catch (e) {
        console.error('Failed to load toolbox PDF', e);
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [topic, token]);

  const acknowledge = async () => {
    try {
      setSubmitting(true);
      setError(null);
      await axios.post(`${API}/toolbox/respond`, { topic_id: topic.id, acknowledged: true }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onAcknowledged?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to acknowledge. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6" style={{ background: '#0B1321' }} data-testid="toolbox-ack-gate">
      <div className="w-full max-w-2xl rounded-2xl border overflow-hidden" style={{ background: '#0F1923', borderColor: '#2D3748' }}>
        <div className="p-5 sm:p-6 border-b" style={{ borderColor: '#2D3748', background: 'linear-gradient(135deg, rgba(30,136,229,0.08), rgba(0,200,150,0.05))' }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,160,0,0.15)' }}>
              <ShieldAlert className="w-5 h-5" style={{ color: '#FFA000' }} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#FFA000' }}>Required Safety Acknowledgement</p>
              <h2 className="text-xl sm:text-2xl font-bold text-white mt-1" data-testid="toolbox-ack-title">{topic.title}</h2>
              <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>
                {new Date(topic.date).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {topic.content_text && (
            <div className="prose prose-invert max-w-none text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#CBD5E1' }} data-testid="toolbox-ack-content">
              {topic.content_text}
            </div>
          )}

          {topic.content_pdf_filename && (
            <div className="rounded-xl p-4 border flex items-center justify-between gap-3" style={{ background: '#0B1321', borderColor: '#2D3748' }}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 flex-shrink-0" style={{ color: '#1E88E5' }} />
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium truncate">Safety Document (PDF)</p>
                  <p className="text-xs truncate" style={{ color: '#64748B' }}>Please review the attached document before acknowledging.</p>
                </div>
              </div>
              {pdfUrl && (
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg text-white"
                  style={{ background: '#1E88E5' }}
                  data-testid="toolbox-ack-pdf-link"
                >
                  <Download className="w-3.5 h-3.5" /> Open
                </a>
              )}
            </div>
          )}

          {!topic.content_text && !topic.content_pdf_filename && (
            <p className="text-sm italic" style={{ color: '#A0AEC0' }}>No additional content provided — please acknowledge you've been briefed.</p>
          )}
        </div>

        <div className="p-5 sm:p-6 border-t flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3" style={{ borderColor: '#2D3748' }}>
          <p className="text-xs" style={{ color: '#A0AEC0' }}>
            By acknowledging, you confirm that you have read and understood today's safety topic.
          </p>
          <Button
            onClick={acknowledge}
            disabled={submitting}
            className="sm:w-auto w-full font-semibold"
            style={{ background: '#00C896' }}
            data-testid="toolbox-ack-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            I Acknowledge
          </Button>
        </div>
        {error && <p className="px-5 pb-4 text-xs text-red-400" data-testid="toolbox-ack-error">{error}</p>}
      </div>
    </div>
  );
}
